import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Searchable single-select with a free-text escape hatch. Hand-rolled for the
 * same reason as `dialog.tsx` — no Radix, no cmdk, no shadcn install.
 *
 * A trigger button shows the current selection's LABEL; the panel carries its
 * own search box. Label and value are kept apart deliberately: the value is an
 * opaque id and the label is what a human reads, so the caller owns both and
 * a selection survives the option list being re-fetched underneath it.
 *
 * The raw-value row is not a nicety. Some refs an operator legitimately needs
 * cannot be enumerated at all (`audit_log:global`, an `estimate:` subject), so
 * a picker that only accepts a pick would remove supported capability.
 */
export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxPanelProps {
  options: ComboboxOption[];
  query: string;
  onQuery: (value: string) => void;
  onPick: (option: ComboboxOption) => void;
  loading?: boolean;
  searchPlaceholder?: string;
  /** Renders a "use this exact value" row so unlistable refs stay reachable. */
  allowRawValue?: boolean;
}

/** The open panel (exported test seam) — pure, no open/close state of its own. */
export function ComboboxPanel({
  options,
  query,
  onQuery,
  onPick,
  loading,
  searchPlaceholder,
  allowRawValue,
}: ComboboxPanelProps) {
  const trimmed = query.trim();
  const isNewValue = allowRawValue && trimmed !== '' && !options.some((o) => o.value === trimmed);
  // A panel is a surface, not a control: it takes `--radius`, never
  // `--radius-ctl` (a pill on warm, which turns a tall list into an oval).
  // `overflow-hidden` keeps the square row backgrounds inside those corners.
  return (
    <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-[var(--radius)] border border-input bg-background shadow-md">
      <div className="border-b p-2">
        <Input
          autoFocus
          aria-label="Search options"
          placeholder={searchPlaceholder ?? 'Search…'}
          className="h-8 shadow-none"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
      </div>
      <ul className="max-h-64 overflow-y-auto py-1" role="listbox">
        {loading && <li className="px-3 py-2 text-sm text-muted-foreground">Searching…</li>}
        {!loading && options.length === 0 && !isNewValue && (
          <li className="px-3 py-2 text-sm text-muted-foreground">No matches.</li>
        )}
        {options.map((option) => (
          <li key={option.value}>
            <button
              type="button"
              role="option"
              className="flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left hover:bg-accent"
              onClick={() => onPick(option)}
            >
              <span className="text-sm">{option.label}</span>
              {option.label !== option.value && (
                <code className="font-mono text-[11px] text-muted-foreground">{option.value}</code>
              )}
            </button>
          </li>
        ))}
        {isNewValue && (
          <li className="border-t">
            <button
              type="button"
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
              onClick={() => onPick({ value: trimmed, label: trimmed })}
            >
              Use <code className="font-mono text-xs">{trimmed}</code>
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}

export interface ComboboxProps {
  value: string;
  /** Human label for `value`; falls back to the raw value when unknown. */
  valueLabel?: string;
  options: ComboboxOption[];
  onSelect: (value: string, label: string) => void;
  /** Server-side search. Omit to filter `options` in the browser. */
  onSearch?: (term: string) => void;
  loading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  allowRawValue?: boolean;
  'aria-label'?: string;
  id?: string;
  className?: string;
}

export function Combobox({
  value,
  valueLabel,
  options,
  onSelect,
  onSearch,
  loading,
  placeholder = 'Select…',
  searchPlaceholder,
  disabled,
  allowRawValue,
  className,
  id,
  ...aria
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const root = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDocument = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocument);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocument);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onQuery = (term: string) => {
    setQuery(term);
    onSearch?.(term);
  };

  // Local filtering only when the caller has not taken over the search.
  const visible = onSearch
    ? options
    : options.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className={cn('relative', className)} ref={root}>
      <button
        type="button"
        id={id}
        aria-label={aria['aria-label']}
        aria-expanded={open}
        disabled={disabled}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-[var(--radius-ctl)] border border-input',
          'bg-background pl-3 pr-3 text-left text-sm shadow-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={cn('truncate', value === '' && 'text-muted-foreground')}>
          {value === '' ? placeholder : (valueLabel ?? '') || value}
        </span>
        <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
      </button>
      {open && (
        <ComboboxPanel
          options={visible}
          query={query}
          onQuery={onQuery}
          loading={loading}
          searchPlaceholder={searchPlaceholder}
          allowRawValue={allowRawValue}
          onPick={(option) => {
            onSelect(option.value, option.label);
            setQuery('');
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}
